import { useEffect, useRef, useState } from "react";
import { useSocketContext } from "../Context/SocketContext";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const SocketNotificationListener = () => {
    const context = useSocketContext();
    const socket = context?.socket;
    const navigate = useNavigate();
    const soundEnabledRef = useRef(false);
    const audioRef = useRef(null); // Persistent audio instance
    const [currentNotification, setCurrentNotification] = useState(null);

    // Redux se user ID fetch karo
    const {
        AuthReducer: { auth },
    } = useSelector((state) => state);

    // User ID extract karo (multiple possible fields check karo)
    const userId = auth?.data?.id;

    useEffect(() => {
        if (!socket) return;

        console.log("[SocketNotificationListener] socket instance:", socket.id, "connected:", socket.connected);

        // Room join function - dynamic userId se
        const joinRoom = () => {
            if (!userId) {
                console.warn("[Socket] User ID not available, cannot join room");
                return;
            }
            const room = `case_manager_${userId}`;
            socket.emit("subscribe", { room: room });
            console.log(`[Socket] Emitted subscribe for room: ${room}`);
        };

        const handleConnect = () => {
            console.log("[Socket] connected:", socket.id);
            // Connect hote hi room join karvao
            joinRoom();
        };

        // Agar socket already connected hai to turant room join karvao
        if (socket.connected) {
            console.log("[Socket] Already connected, joining room immediately");
            joinRoom();
        }

        const handleDisconnect = (reason) => {
            console.log("[Socket] disconnected:", reason);
        };

        const handleAny = (event, ...args) => {
            console.log("[Socket] event received:", event, args);
        };

        const handleUserWalletDetails = (data) => {
            console.log("[Socket] POPUP_DATA payload:", data);

            // Play sound if enabled
            if (soundEnabledRef.current && audioRef.current) {
                try {
                    // Reset to start if already playing
                    if (!audioRef.current.paused) {
                        audioRef.current.currentTime = 0;
                    }
                    // Play sound
                    audioRef.current.play().catch((err) => {
                        console.warn("Notification sound play failed:", err);
                        // Retry once after a small delay
                        setTimeout(() => {
                            if (audioRef.current) {
                                audioRef.current.play().catch((e) => {
                                    console.warn("Notification sound retry failed:", e);
                                });
                            }
                        }, 100);
                    });
                } catch (e) {
                    console.warn("Notification sound error:", e);
                }
            }

            // Save notification to show in center modal
            setCurrentNotification(data);
        };

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.onAny(handleAny);
        socket.on("POPUP_DATA", handleUserWalletDetails);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.offAny(handleAny);
            socket.off("POPUP_DATA", handleUserWalletDetails);
        };
    }, [socket, navigate, userId]);

    // Initialize persistent audio instance
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio("/notification.mp3");
            audioRef.current.volume = 1.0;
            audioRef.current.preload = "auto";

            // Reset audio on end so it can be played again
            audioRef.current.addEventListener("ended", () => {
                audioRef.current.currentTime = 0;
            });
        }
    }, []);

    // Auto-enable sound on first user interaction (anywhere in the page)
    useEffect(() => {
        if (soundEnabledRef.current) return;

        const enableOnInteraction = async () => {
            if (soundEnabledRef.current || !audioRef.current) return;
            try {
                // Prime audio with a user gesture so future plays are allowed
                audioRef.current.volume = 0.0;
                await audioRef.current.play();
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current.volume = 1.0;
                soundEnabledRef.current = true;
                console.log("[Socket] Notification sound primed on user interaction");
            } catch (err) {
                console.warn("Notification sound prime failed:", err);
            }
        };

        window.addEventListener("click", enableOnInteraction, { once: true });
        window.addEventListener("keydown", enableOnInteraction, { once: true });
        window.addEventListener("touchstart", enableOnInteraction, { once: true });

        return () => {
            window.removeEventListener("click", enableOnInteraction);
            window.removeEventListener("keydown", enableOnInteraction);
            window.removeEventListener("touchstart", enableOnInteraction);
        };
    }, []);

    if (!currentNotification) {
        return null;
    }

    const title = currentNotification.message || "New Wallet Notification";

    const n = currentNotification || {};

    // camelCase / snake_case key ko readable label me convert karega
    const toLabel = (key) => {
        return key
            .replace(/_/g, " ") // snake_case -> space
            .replace(/([A-Z])/g, " $1") // camelCase -> space before caps
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case
    };

    const detailRows = Object.entries(n)
        .filter(([key, value]) => {
            if (key === "message") return false; // title me already use
            return value !== undefined && value !== null && value !== "";
        })
        .map(([key, value]) => {
            if (key === "timestamp") {
                try {
                    value = new Date(value).toLocaleString();
                } catch {
                    // ignore, raw value dikhayenge
                }
            }
            return {
                label: toLabel(key),
                value,
            };
        });

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(15, 23, 42, 0.45)", // darker overlay
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
                padding: 12,
            }}
        >
            <div
                style={{
                    backgroundColor: "#ffffff",
                    borderRadius: 10,
                    padding: 16,
                    minWidth: 500,
                    maxWidth: 500,
                    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.35)",
                    border: "1px solid rgba(148, 163, 184, 0.4)",
                    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: "999px",
                            background:
                                "linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #0ea5e9 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 18,
                            marginRight: 10,
                        }}
                    >
                        🔔
                    </div>
                    <div>
                        <div
                            style={{
                                fontWeight: 600,
                                fontSize: 15,
                                color: "#0f172a",
                            }}
                        >
                            {title}
                        </div>
                        <div
                            style={{
                                fontSize: 11,
                                color: "#64748b",
                                marginTop: 2,
                            }}
                        >
                            Real‑time notification
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        maxHeight: 220,
                        overflowY: "auto",
                        padding: "8px 10px",
                        marginBottom: 12,
                        backgroundColor: "#f8fafc",
                        borderRadius: 8,
                        border: "1px solid rgba(226, 232, 240, 0.9)",
                    }}
                >
                    {detailRows.map((row, index) => (
                        <div
                            key={row.label}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                                fontSize: 12,
                                padding: "4px 0",
                                borderBottom:
                                    index !== detailRows.length - 1
                                        ? "1px dashed rgba(203, 213, 225, 0.7)"
                                        : "none",
                            }}
                        >
                            <span
                                style={{
                                    fontWeight: 500,
                                    color: "#64748b",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {row.label}
                            </span>
                            <span
                                style={{
                                    color: "#0f172a",
                                    fontWeight: 500,
                                    textAlign: "right",
                                    flex: 1,
                                }}
                            >
                                {row.value}
                            </span>
                        </div>
                    ))}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                    {currentNotification.url ? (
                        <button
                            type="button"
                            onClick={() => {
                                try {
                                    if (
                                        currentNotification.url.startsWith("http://") ||
                                        currentNotification.url.startsWith("https://")
                                    ) {
                                        window.open(currentNotification.url, "_blank", "noopener,noreferrer");
                                    } else {
                                        navigate(currentNotification.url);
                                    }
                                } catch (e) {
                                    // ignore
                                }
                                // Sirf Open par hi close karein
                                setCurrentNotification(null);
                            }}
                            style={{
                                padding: "6px 14px",
                                fontSize: 12,
                                borderRadius: 999,
                                border: "none",
                                background:
                                    "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            Open
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                // Noted: sirf close kar do
                                setCurrentNotification(null);
                            }}
                            style={{
                                padding: "6px 14px",
                                fontSize: 12,
                                borderRadius: 999,
                                border: "none",
                                backgroundColor: "#94a3b8",
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            Noted
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SocketNotificationListener;


